import React, { useEffect, useState, useRef } from 'react';
import './PlatformValue.css';

const PlatformValue = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    communities: 0,
    uptime: 0,
    features: 0
  });
  const sectionRef = useRef(null);
  const animationRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          setIsVisible(true);
          hasAnimatedRef.current = true;
          animateCounters();
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, []);

  const animateCounters = () => {
    const targets = { communities: 250, uptime: 99.9, features: 60 };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let step = 0;
    animationRef.current = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounters({
        communities: Math.floor(targets.communities * progress),
        uptime: (targets.uptime * progress).toFixed(1),
        features: Math.floor(targets.features * progress)
      });

      if (step >= steps) {
        clearInterval(animationRef.current);
        animationRef.current = null;
        setCounters(targets);
      }
    }, stepDuration);
  };

  const valueProps = [
    {
      icon: '🔗',
      title: 'Unified Ecosystem',
      description: 'All products work seamlessly together, sharing data and capabilities for a cohesive experience across platforms.',
      benefits: ['Single sign-on', 'Shared user profiles', 'Cross-platform messaging']
    },
    {
      icon: '🚀',
      title: 'Scalable Infrastructure',
      description: 'Built on modern, cloud-native architecture that scales from small communities to enterprise deployments.',
      benefits: ['Auto-scaling', '99.9% uptime', 'Global CDN']
    },
    {
      icon: '🔒',
      title: 'Enterprise Security',
      description: 'Security-first architecture with encryption, identity controls, and operational safeguards built into every layer.',
      benefits: ['Data encryption', 'Privacy controls', '2FA authentication']
    },
    {
      icon: '🎨',
      title: 'White-Label Ready',
      description: 'Customize the platform with your branding, colors, and domain for a completely personalized experience.',
      benefits: ['Custom branding', 'Your domain', 'Tailored features']
    },
    {
      icon: '📊',
      title: 'Advanced Analytics',
      description: 'Deep insights into community engagement, user behavior, and content performance with real-time dashboards.',
      benefits: ['Real-time metrics', 'Custom reports', 'Data export']
    },
    {
      icon: '🤝',
      title: 'Dedicated Support',
      description: 'Enterprise customers get dedicated account managers, priority support, and customization assistance.',
      benefits: ['24/7 support', 'Account manager', 'Custom development']
    }
  ];

  return (
    <section
      id="platform-value"
      className={`platform-value ${isVisible ? 'visible' : ''}`}
      ref={sectionRef}
      aria-labelledby="platform-value-title"
    >
      <div className="platform-value-container">
        {/* Header */}
        <div className="value-header">
          <span className="value-badge">Why Choose ZoneZapp</span>
          <h2 id="platform-value-title" className="value-title">
            More Than Software.<br />
            <span className="gradient-text">A Complete Ecosystem.</span>
          </h2>
          <p className="value-description">
            Built for scale, designed for communities, and structured for enterprise execution.
            ZoneZapp gives your team one operating layer to connect, engage, and deliver outcomes.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <div className="stat-icon">🌍</div>
            <div className="stat-content">
              <div className="stat-number">{counters.communities}+</div>
              <div className="stat-label">Active Communities</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-number">{counters.uptime}%</div>
              <div className="stat-label">Platform Uptime</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon">✨</div>
            <div className="stat-content">
              <div className="stat-number">{counters.features}+</div>
              <div className="stat-label">Powerful Features</div>
            </div>
          </div>
        </div>

        {/* Value Props Grid */}
        <div className="value-props-grid">
          {valueProps.map((prop, index) => (
            <div
              key={index}
              className="value-prop-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="value-prop-icon">{prop.icon}</div>
              <h3 className="value-prop-title">{prop.title}</h3>
              <p className="value-prop-description">{prop.description}</p>
              <ul className="value-prop-benefits">
                {prop.benefits.map((benefit, idx) => (
                  <li key={idx}>
                    <i className="fas fa-check"></i>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Technology Stack removed per request */}

        {/* CTA Banner */}
        <div className="value-cta-banner">
          <div className="value-cta-content">
            <h3>Ready to Transform Your Community Operations?</h3>
            <p>Partner with ZoneZapp to launch a secure, scalable, and measurable engagement platform.</p>
          </div>
          <div className="value-cta-actions">
            <a href="/contact-us" className="cta-button primary">
              Schedule a Demo
              <i className="fas fa-calendar-alt"></i>
            </a>
            <a href="/business" className="cta-button secondary">
              View Pricing
              <i className="fas fa-tag"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformValue;
